import React, { useState, useEffect } from 'react';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { Form, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSpinner, FaPlus, FaDownload, FaUserPlus, FaEnvelope, FaPhone, FaUserTag } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuthContext } from '../../context/AuthContext';
import { kamatiApi } from '../../api/kamati';
import './kamati.css';
import { useDebounce } from '../../hooks/useDebounce';


const Kamati = () => {
  const { user, events, isAuthenticated } = useAuthContext();
  const currentEvent = events?.[0];

  // State for data
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state for new member
  const [newMember, setNewMember] = useState({
    full_name: '',
    mobile: '',
    role: '',
    email: ''
  });

  // Role options matching API definitions:
  // 1: Mratibu, 2: Mwenyekiti, 3: Katibu, 4: Mhazini, 5: Mjumbe
  const roleOptions = [
    { value: 1, label: 'Mratibu' },
    { value: 2, label: 'Mwenyekiti' },
    { value: 3, label: 'Katibu' },
    { value: 4, label: 'Mhazini' },
    { value: 5, label: 'Mjumbe' }
  ];

  const isFirstRender = React.useRef(true);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch when debounced search term changes
  useEffect(() => {
    if (!isAuthenticated || !currentEvent) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchCommitteeMembers(1, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, isAuthenticated, currentEvent]);


  // Fetch committee members on component mount or page change
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchCommitteeMembers(currentPage, searchTerm);
    } else if (isAuthenticated && !currentEvent) {
      // No event selected yet
      setLoading(false);
      setCommitteeMembers([]);
    }
  }, [isAuthenticated, currentEvent, currentPage]);

  const fetchCommitteeMembers = async (page = 1, search = '') => {
    if (committeeMembers.length === 0) setLoading(true);
    setError(null);
    try {
      const response = await kamatiApi.getCommitteeMembers(page, search);

      // Handle different response structures
      let members = [];
      let fetchedTotal = 0;
      if (response?.data?.data?.comitees) {
        members = response.data.data.comitees;
        fetchedTotal = response.data.total_records || 0;
      } else if (response?.data?.comitees) {
        members = response.data.comitees;
        fetchedTotal = response.data.total_records || 0;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        members = response.data.data;
        fetchedTotal = response.data.total_records || response.data.data.length;
      } else if (response?.data && Array.isArray(response.data)) {
        members = response.data;
        fetchedTotal = response.data.length;
      } else if (Array.isArray(response)) {
        members = response;
        fetchedTotal = response.length;
      }
      setTotalRecords(fetchedTotal);

      // Transform data to match component structure (handle null/undefined values)
      const formattedMembers = members.map(member => ({
        id: member.id || member.member_id,
        name: member.full_name || member.name || 'N/A',
        phone: member.mobile || member.phone || member.phone_number || 'N/A',
        email: member.email || 'N/A',
        role: getRoleName(member.comitee_role || member.role),
        role_id: member.comitee_role || member.role,
        status: member.status === 1 ? 'Active' : (member.status === 0 ? 'Inactive' : 'Pending'),
        status_code: member.status,
        created_at: member.created_at,
        token: member.token
      }));

      setCommitteeMembers(formattedMembers);

      if (formattedMembers.length === 0) {
        // No data message will be shown in the table
        console.log('No committee members found');
      }
    } catch (err) {
      console.error('Error fetching committee members:', err);
      // Don't show error for empty data, just set empty array
      setCommitteeMembers([]);
      if (err.response?.status !== 404) {
        setError('Failed to load committee members. Please refresh the page.');
        toast.error('Failed to load committee members');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRoleName = (roleId) => {
    if (!roleId) return 'Mjumbe';
    const roles = {
      1: 'Mratibu',
      2: 'Mwenyekiti',
      3: 'Katibu',
      4: 'Mhazini',
      5: 'Mjumbe'
    };
    return roles[roleId] || 'Mjumbe';
  };

  const getRoleId = (roleName) => {
    const roles = {
      'Mratibu': 1,
      'Mwenyekiti': 2,
      'Katibu': 3,
      'Mhazini': 4,
      'Mjumbe': 5
    };
    return roles[roleName] || 5;
  };

  // Add new committee member
  const handleAddMember = async () => {
    if (!newMember.full_name.trim()) {
      toast.warning('Tafadhali ingiza jina la mwanakamati');
      return;
    }

    if (!newMember.mobile.trim()) {
      toast.warning('Tafadhali ingiza namba ya simu');
      return;
    }

    setSubmitting(true);
    try {
      const memberData = {
        full_name: newMember.full_name.trim(),
        mobile: newMember.mobile.trim(),
        role: newMember.role || 5,
        email: newMember.email || null,
        event_id: currentEvent?.id
      };

      const response = await kamatiApi.addCommitteeMember(memberData);

      if (handleApiResponse(response)) {
        setShowModal(false);
        resetForm();
        fetchCommitteeMembers();
      }
    } catch (err) {
      console.error('Error adding member:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Update committee member
  const handleUpdateMember = async () => {
    if (!selectedMember) return;

    setSubmitting(true);
    try {
      const updateData = {
        full_name: selectedMember.name,
        mobile: selectedMember.phone,
        role: getRoleId(selectedMember.role),
        email: selectedMember.email !== 'N/A' ? selectedMember.email : null,
        event_id: currentEvent?.id
      };

      const response = await kamatiApi.updateCommitteeMember(selectedMember.id, updateData);

      if (handleApiResponse(response)) {
        setShowEditModal(false);
        setSelectedMember(null);
        fetchCommitteeMembers();
      }
    } catch (err) {
      console.error('Error updating member:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete committee member
  const handleDeleteMember = async () => {
    if (!selectedMember) return;

    setSubmitting(true);
    try {
      const response = await kamatiApi.deleteCommitteeMember(selectedMember.id);

      if (handleApiResponse(response)) {
        setShowDeleteModal(false);
        setSelectedMember(null);
        fetchCommitteeMembers();
      }
    } catch (err) {
      console.error('Error deleting member:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Resend invitation token
  const handleResendInvitation = async (member) => {
    if (!member.token) {
      toast.info('Token haipatikani. Mwanakamati anaweza kuwa tayari ameamilishwa.');
      return;
    }

    try {
      // You can implement resend invitation API call here
      // await kamatiApi.resendInvitation(member.id);
      toast.success('Mwaliko umetumwa tena kwa mwanakamati');
    } catch (err) {
      console.error('Error resending invitation:', err);
      toast.error('Imeshindwa kutuma mwaliko tena');
    }
  };

  const resetForm = () => {
    setNewMember({
      full_name: '',
      mobile: '',
      role: '',
      email: ''
    });
  };

  const columns = [
    {
      name: '#',
      cell: (row, index) => index + 1,
      width: '60px'
    },
    {
      name: 'Jina Kamili',
      selector: (row) => row.name,
      sortable: true,
      grow: 2
    },
    {
      name: 'Namba ya Simu',
      selector: (row) => row.phone,
      sortable: true
    },
    {
      name: 'Nafasi / Wadhifa',
      selector: (row) => row.role,
      sortable: true
    },
    {
      name: 'Hali',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span className={`status-badge status-${row.status.toLowerCase()}`}>
          {row.status === 'Active' ? 'Active' : row.status === 'Inactive' ? 'Inactive' : 'Anasubiri'}
        </span>
      )
    },
    {
      name: 'Vitendo',
      cell: (row) => (
        <div className="action-buttons">
          <FaEdit
            className="text-primary action-icon"
            onClick={() => {
              setSelectedMember(row);
              setShowEditModal(true);
            }}
            title="Hariri Mwanakamati"
          />
          <FaTrash
            className="text-danger action-icon"
            onClick={() => {
              setSelectedMember(row);
              setShowDeleteModal(true);
            }}
            title="Futa Mwanakamati"
          />
        </div>
      ),
      width: '100px'
    },
  ];

  const filteredData = committeeMembers.filter(member =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone?.includes(searchTerm)
  );

  // Custom empty state component
  const EmptyState = () => (
    <div className="empty-state-container">
      <div className="empty-state-icon">
        <FaUserTag size={64} className="text-muted" />
      </div>
      <h5 className="empty-state-title">Hakuna Wanakamati</h5>
      <p className="empty-state-message">
        Bado hakuna mwanakamati aliyeongezwa. Bonyeza kitufe cha "Ongeza Mwanakamati" kuanza.
      </p>
      <Button
        variant="primary"
        onClick={() => setShowModal(true)}
        className="empty-state-btn"
      >
        <FaPlus className="me-2" />
        Ongeza Mwanakamati wa Kwanza
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="kamati-loading">
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Inapakia wanakamati...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kamati-container">
      <div className="container-fluid mt-4">
        <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>
          Kamati ya Maandalizi
        </h1>

        {/* Action Buttons */}
        <div className="kamati-actions">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowModal(true)}
            className="action-btn"
          >
            <FaUserPlus className="me-2" />
            Ongeza Mwanakamati
          </Button>
        </div>

        {/* Search Box - Only show if there are members */}
        {committeeMembers.length > 0 && (
          <div className="kamati-search">
            <Form.Group controlId="searchInput">
              <Form.Label className="fw-semibold">Tafuta Mwanakamati</Form.Label>
              <div className="search-input-wrapper">
                <Form.Control
                  type="text"
                  placeholder="Tafuta kwa jina au namba ya simu..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </Form.Group>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mt-3" onClose={() => setError(null)} dismissible>
            <Alert.Heading>Hitilafu!</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" size="sm" onClick={fetchCommitteeMembers}>
              Jaribu Tena
            </Button>
          </Alert>
        )}

        {/* Data Table */}
        <div className="kamati-table-wrapper">
          {committeeMembers.length > 0 ? (
            <StyledDataTable
              columns={columns}
              data={filteredData}
              paginationServer
              paginationTotalRows={totalRecords}
              paginationPerPage={perPage}
              paginationDefaultPage={currentPage}
              onChangePage={(page) => setCurrentPage(page)}
              paginationRowsPerPageOptions={[]}
              paginationComponentOptions={{
                noRowsPerPage: true,
                rangeSeparatorText: 'ya',
                selectAllRowsItem: false,
              }}
              emptyMessage="Hakuna matokeo yanayolingana na utafutaji wako."
              pageInfo={
                totalRecords > 0
                  ? `Ukurasa ${currentPage} kati ya ${Math.ceil(totalRecords / perPage)} · Jumla ya wanakamati: ${totalRecords}`
                  : undefined
              }
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <FaUserPlus className="me-2" style={{ color: '#4169e1' }} />
            Ongeza Mwanakamati Mpya
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FaUserTag className="me-2" />
                Jina Kamili <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Mfano: John M. Doe"
                value={newMember.full_name}
                onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FaPhone className="me-2" />
                Namba ya Simu <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="tel"
                placeholder="Mfano: 0712345678"
                value={newMember.mobile}
                onChange={(e) => setNewMember({ ...newMember, mobile: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                Weka namba ya simu kwa mfumo wa 0712345678
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FaEnvelope className="me-2" />
                Barua Pepe (Si Lazima)
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Mfano: johndoe@example.com"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Nafasi / Wadhifa</Form.Label>
              <Form.Select
                value={newMember.role}
                onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
              >
                <option value="">Chagua Nafasi</option>
                {roleOptions.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Ghairi
          </Button>
          <Button
            variant="primary"
            onClick={handleAddMember}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Ongeza Mwanakamati'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Member Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <FaEdit className="me-2" style={{ color: '#4169e1' }} />
            Hariri Taarifa za Mwanakamati
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMember && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Jina Kamili</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedMember.name}
                  onChange={(e) => setSelectedMember({ ...selectedMember, name: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Namba ya Simu</Form.Label>
                <Form.Control
                  type="tel"
                  value={selectedMember.phone}
                  onChange={(e) => setSelectedMember({ ...selectedMember, phone: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Barua Pepe</Form.Label>
                <Form.Control
                  type="email"
                  value={selectedMember.email !== 'N/A' ? selectedMember.email : ''}
                  onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Nafasi / Wadhifa</Form.Label>
                <Form.Select
                  value={getRoleId(selectedMember.role)}
                  onChange={(e) => {
                    const roleName = getRoleName(parseInt(e.target.value));
                    setSelectedMember({ ...selectedMember, role: roleName });
                  }}
                >
                  {roleOptions.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Ghairi
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateMember}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Sasisha Taarifa'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Member Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" />
            Futa Mwanakamati
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMember && (
            <div>
              <p>Je, una uhakika unataka kumfuta mwanakamati huyu?</p>
              <div className="member-details-card p-3 rounded">
                <div className="d-flex align-items-center mb-2">
                  <strong className="w-25">Jina:</strong>
                  <span>{selectedMember.name}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <strong className="w-25">Simu:</strong>
                  <span>{selectedMember.phone}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <strong className="w-25">Nafasi:</strong>
                  <span>{selectedMember.role}</span>
                </div>
              </div>
              <p className="text-danger mt-3 mb-0">
                <strong>Kumbuka:</strong> Kitendo hiki hakiwezi kutenguliwa.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Ghairi
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteMember}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Futa Mwanakamati'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Kamati;